"use client";
import {
  Image,
  Text,
  Flex,
  Button,
  Skeleton,
  SkeletonText,
  Box,
  useDisclosure,
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  Spinner,
  ModalBody,
  useMediaQuery,
} from "@chakra-ui/react";
import Head from 'next/head';
import { useRouter, usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { useAppContext } from "hooks";
import { MinusIcon, AddIcon } from "@chakra-ui/icons";
import { useGetModel, useGetForumGenerations, useGetModelImages } from "queries";
import { SocialMediaAccount } from "types";
import { Jumbotron, Generator, GoogleForm, Navbar, PromptModal } from "components";
import { LuInstagram, LuTwitter } from "react-icons/lu";
import Link from "next/link";

const ModelPage = ({ name }: { name: string }) => {
  const pathname = usePathname();
  const router = useRouter();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const { jwt, profile } = useAppContext();
  const { data: model, isLoading } = useGetModel(pathname.includes("mymodels"), name);
  const {
    data: modelCreations,
    isLoading: isCreationsLoading,
  } = useGetForumGenerations(isMobile ? 4 : 8, pathname.includes("mymodels") ? model?.id : name, pathname.includes("mymodels"));
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isPromptModalOpen, onOpen: openPromptModal, onClose: closePromptModal } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState("");
  const [generatedImages, setGeneratedImages] = useState<any>([]);
  const [generatedImagesUpscaled, setGeneratedImagesUpscaled] = useState<boolean[]>([]);
  const [imagesFileName, setImagesFileName] = useState<string[]>([]);

  const { 
    data: imagesData, 
    isLoading: isImagesLoading 
  } = useGetModelImages(
    (!pathname.includes("mymodels") || !profile?.nsfw_consent) ? name : undefined
  );

  useEffect(() => {
    if (!isLoading && (!model || (!model.checkpoints || !model.checkpoints.length))) {
      router.replace('/'); // Redirect to the homepage if the model is pending or doesn't exist
    }
  }, [isLoading, model, router]);

  useEffect(() => {
    // Use API data for user's own models when they have nsfw consent
    if (jwt && name && profile?.nsfw_consent && pathname.includes("mymodels")) {
      const image_generations = modelCreations?.pages
        ?.map((page: any) => page.image_generations)
        .flat();

      setGeneratedImages(image_generations?.map((img: any) => img?.sas_url));
      setGeneratedImagesUpscaled(
        image_generations?.map((img: any) => Boolean(img?.upscaled)) ?? []
      );
      setImagesFileName(image_generations?.map((img: any) => img?.file_name) ?? []);
    } 
    // Use the backend-fetched images for public models or when user doesn't have nsfw consent
    else if (imagesData?.images) {
      setGeneratedImages(imagesData.images);
    }
  }, [profile?.nsfw_consent, jwt, name, modelCreations?.pages, pathname, imagesData]);

  const onOpenImage = (image: string, fileName: string) => {
    setSelectedImage(image);
    openPromptModal();
  };

  const totalImageGenerations = (modelCreations?.pages?.[0] as any)?.total_image_generations || "";

  if (isLoading || !model || (!model.checkpoints || !model.checkpoints.length)) {
    // If still loading, the model is pending, or the model is not found, you can either return null or show a skeleton loader.
    return (
      <Flex direction="column" align="center" justify="center" minH="100vh">
        <h1 style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
    Explore AI Character Model
    </h1>
        <Spinner size="xl" thickness="4px" speed="0.65s" color="teal.500" mb={4} />
        <Text fontSize="xl" fontWeight="bold">
          Loading...
        </Text>
      </Flex>
    );
  }

  return (
    
    <Flex direction="column">
    <h1 style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
    Model AI Images | DeepMode AI Character Reimagined
    </h1>
      <Head>
        <title>{model ? `${model.display_name} AI Images | DeepMode AI Character Reimagined` : 'Model Page'}</title>
        <meta name="description" content={model ? `Discover ${model.display_name}, DeepMode's advanced AI-generated character. Explore a gallery of stunning digital art showcasing this unique persona in diverse styles and settings. Experience cutting-edge AI model training and character development. Create your own AI art with our innovative generator today!` : 'Model Page'} />
      </Head>
      {isMobile ? (
        <Flex direction="column">
          <Navbar />
          <Flex  align="center" direction="column">
            {isLoading ? (
              <Skeleton mt="50px" h="200px" w="200px" />
            ) : (
              <Image
                mt="50px"
                h="200px"
                w="200px"
                borderRadius="15px"
                src={model?.picture || "default_image_url"}
                alt=""
                objectFit="contain"
              />
            )}
            {isLoading ? (
              <SkeletonText mt="25px" noOfLines={1} fontSize="56px" w="300px" />
            ) : (
              <Text
                mt="25px"
                style={{
                  background: "linear-gradient(to right,  #FFE1FF 10%, #A4DBEE 50%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  MozBackgroundClip: "text",
                }}
                fontSize="28px"
                fontWeight="extrabold"
              >
                {model?.display_name}
              </Text>
            )}
            <Flex>
              {model?.social_media_accounts?.map((social: SocialMediaAccount, index: number) => (
                <Box w="20px" key={index}>
                  <Link target="_blank" href={social.link}>
                    {social.social_media.name === "twitter" ? (
                      <LuTwitter style={{ marginRight: "10px" }} size={20} />
                    ) : (
                      <LuInstagram size={20} />
                    )}
                  </Link>
                </Box>
              ))}
            </Flex>
            {isLoading ? (
              <SkeletonText mb="40px" mt="25px" noOfLines={4} spacing="4" />
            ) : (
              <Text mb="40px" mt="25px" color="#7E95AA" fontSize="13px">
                {model?.description}
              </Text>
            )}
            {!pathname.includes("create") && (
              <Button
                mt="25px"
                p={6}
                borderRadius="16px"
                color="white"
                bg="#FF1B6D"
                onClick={() => (!jwt ? onOpen() : router.push(`${pathname}/${model?.isFlux ? "create-flux" : "create"}`))}
              >
                
                <Text fontSize="16px" fontWeight="bold">
                  Play with this model
                </Text>
              </Button>
            )}
            <Flex h="100%" direction="column">
              {pathname.includes("create") && model && model.checkpoints && jwt ? (
                <Generator
                  id={model.id}
                  gender={model.model_category.name}
                  checkpoints={model.checkpoints}
                />
              ) : (
                <Flex align="center" direction="column">
                  <Flex w="100%" justify="space-evenly" mt="20px">
                    <Text
                      color="white"
                      fontSize="16px"
                      fontWeight="semibold"
                    >
                      Created by users
                    </Text>
                  </Flex>
                  {totalImageGenerations.toLocaleString() ? (
                    <Box mt="30px" p={2} borderRadius="18px" border="1px solid white">
                      {totalImageGenerations.toLocaleString()} Creations
                    </Box>
                  ) : (
                    <></>
                  )}
                  <Grid
                    mt="50px"
                    gap={2}
                    templateColumns={{
                      base: "repeat(2,1fr)",
                      lg: "repeat(4,1fr)",
                    }}
                  >
                    {isCreationsLoading
                      ? [...Array(4)].map((_, index) => (
                          <Skeleton key={index} w="160px" height="240px" borderRadius="14px" />
                        ))
                      : generatedImages?.map((image: string, index: number) => (
                          <Flex key={index} position="relative">
                            <Image
                              _hover={{ cursor: "pointer" }}
                              src={image}
                              onClick={() => onOpenImage(image, imagesFileName[index])}
                              alt={`Generated Image ${index}`}
                              borderRadius="14px"
                              objectFit="cover"
                            />
                            {profile?.nsfw_consent && pathname.includes("mymodels") && (
                              <Flex top={2} right={2} position="absolute">
                                <PromptModal 
                                  isOpen={isPromptModalOpen}
                                  onClose={closePromptModal}
                                  isFlux={model.isFlux} 
                                  model_id={model.id} 
                                  generationImage={image} 
                                  file_name={imagesFileName[index]} 
                                />
                              </Flex>
                            )}
                            {index < generatedImagesUpscaled.length &&
                              generatedImagesUpscaled[index] && (
                                <Flex
                                  position="absolute"
                                  top={2} // Position the button at the bottom of the container
                                  right={2} // And to the right
                                  bgColor="black"
                                  w="24px"
                                  h="16px"
                                  justifyContent="center"
                                  alignItems="center"
                                  border="1px solid yellow"
                                  borderRadius="2px"
                                >
                                  <Text
                                    w="100%"
                                    fontSize="12px"
                                    fontWeight="bold"
                                    color="yellow"
                                    textAlign="center"
                                  >
                                    HD
                                  </Text>
                                </Flex>
                              )}
                          </Flex>
                        ))}
              
                  </Grid>
                  {!pathname.includes("create") && (
                    <Button
                      mt="25px"
                      p={6}
                      borderRadius="16px"
                      color="white"
                      bg="#FF1B6D"
                      onClick={() => (!jwt ? onOpen() : router.push(`${pathname}/${model?.isFlux ? "create-flux" : "create"}`))}
                    >
                      <Text fontSize="16px" fontWeight="bold">
                        Play with this model
                      </Text>
                    </Button>
                  )}
                </Flex>
              )}
              <Box h="200px" />
              <GoogleForm onClose={onClose} isOpen={isOpen} />
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <>
          <Head>
            <title>{model ? `${model.display_name} AI Images | DeepMode AI Character Reimagined` : 'Model Page'}</title>
            <meta name="description" content={model ? `Discover ${model.display_name}, DeepMode's advanced AI-generated character. Explore a gallery of stunning digital art showcasing this unique persona in diverse styles and settings. Experience cutting-edge AI model training and character development. Create your own AI art with our innovative generator today!` : 'Model Page'} />
          </Head>
          <Jumbotron background={"linear-gradient(to bottom, #16181F, #121C3D)"}>
            <Flex w="100%" h="350px" align="center">
              <Flex justify="space-between" w="100%" align="end">
                <Flex align="end">
                  {isLoading ? (
                    <Skeleton h="200px" w="200px" />
                  ) : (
                    <Image
                      h="200px"
                      w="200px"
                      src={model?.picture || "default_image_url"}
                      objectFit="contain"
                      borderRadius="12px"
                    />
                  )}
                  <Flex justify="space-between" zIndex={2} ml="50px" direction="column">
                    {isLoading ? (
                      <SkeletonText noOfLines={1} fontSize="56px" w="300px" />
                    ) : (
                      <Text
                        style={{
                          background: "linear-gradient(to right,  #FFE1FF 10%, #A4DBEE 50%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          MozBackgroundClip: "text",
                        }}
                        fontSize="56px"
                        fontWeight="extrabold"
                      >
                        {model?.display_name}
                      </Text>
                    )}
                    <Flex>
                      {isLoading ? (
                        <Skeleton h="20px" w="20px" mb="4" />
                      ) : (
                        model?.social_media_accounts?.map(
                          (social: SocialMediaAccount, index: number) => (
                            <Box w="20px" key={index}>
                              <Link target="_blank" href={social.link}>
                                {social.social_media.name === "twitter" ? (
                                  <LuTwitter />
                                ) : (
                                  <LuInstagram />
                                )}
                              </Link>
                            </Box>
                          )
                        )
                      )}
                    </Flex>
                    {isLoading ? (
                      <SkeletonText mt="4" noOfLines={4} spacing="4" />
                    ) : (
                      <Text maxW="550px" color="#7E95AA" fontSize="13px" w="650px" mt="20px">
                        {model?.description}
                      </Text>
                    )}
                  </Flex>
                </Flex>
                 
              
              </Flex>
            </Flex>
          </Jumbotron>
          <Flex h="100%" direction="column" bg="#050918">
            {pathname.includes("create") && model && model.checkpoints && jwt ? (
              <Generator
                id={model.id}
                gender={model.model_category.name}
                checkpoints={model.checkpoints}
              />
            ) : (
              <Flex
                px={["25px", "35px", "40px", "60px", "60px", "18%"]}
                align="center"
                direction="column"
              >
                <Flex w="60%" justify="space-around">
                  <Text
                    color="white"
                    mt="50px"
                    fontSize="32px"
                    fontWeight="semibold"
                  >
                    Created by users
                  </Text>
                </Flex>
                {totalImageGenerations.toLocaleString() ? (
                  <Box mt="30px" p={2} borderRadius="18px" border="1px solid white">
                    {totalImageGenerations.toLocaleString()} Creations
                  </Box>
                ) : (
                  <></>
                )}
                <Grid mt="50px" gap={2} templateColumns={"repeat(4,1fr)"}>
                  {isCreationsLoading
                    ? [...Array(8)].map((_, index) => (
                        <Skeleton
                          key={index}
                          w={{ md: "220px", lg: "260px" }}
                          height="380px"
                          borderRadius="14px"
                        />
                      ))
                    : generatedImages?.map((image: string, index: number) => (
                        <Flex position="relative" key={index}>
                          {profile?.nsfw_consent && pathname.includes("mymodels") && (
                            <Flex
                              top={5}
                              left={5}
                              position="absolute"
                            >
                              <PromptModal
                                isOpen={isPromptModalOpen}
                                onClose={closePromptModal}
                                isFlux={model.isFlux} 
                                model_id={model.id} 
                                generationImage={image} 
                                file_name={imagesFileName[index]} 
                              />
                            </Flex>
                          )}
                          <Image
                            _hover={{ cursor: "pointer" }}
                            src={image}
                            onClick={() => onOpenImage(image, imagesFileName[index])}
                            alt={`Generated Image ${index}`}
                            borderRadius="14px"
                            objectFit="cover"
                          />
                          {index < generatedImagesUpscaled.length &&
                            generatedImagesUpscaled[index] && (
                              <Flex
                                position="absolute"
                                top={{ base: "2", md: "5" }} // Position the button at the bottom of the container
                                right={{ base: "2", md: "5" }} // And to the right
                                bgColor="black"
                                w="36px"
                                h="24px"
                                justifyContent="center"
                                alignItems="center"
                                border="2px solid yellow"
                                borderRadius="4px"
                              >
                                <Text
                                  w="100%"
                                  fontSize="16px"
                                  fontWeight="bold"
                                  color="yellow"
                                  textAlign="center"
                                >
                                  HD
                                </Text>
                              </Flex>
                            )}
                        </Flex>
                      ))}
                </Grid>
                {!pathname.includes("create") && (
                  <Button
                    mt="25px"
                    p={6}
                    borderRadius="16px"
                    color="white"
                    bg="#FF1B6D"
                    onClick={() => (!jwt ? onOpen() : router.push(`${pathname}/${model?.isFlux ? "create-flux" : "create"}`))}
                  >
                    <Text fontSize="16px" fontWeight="bold">
                      Play with this model
                    </Text>
                  </Button>
                )}
              </Flex>
            )}
            <Box h="200px" />
            <GoogleForm onClose={onClose} isOpen={isOpen} />
          </Flex>
        </>
      )}
      <CustomAccordion/>
    </Flex>
  );
};

const CustomAccordion = () => {
  const faqData = [
      {
        question: "What is DeepMode and how does it work?",
        answer: (
          <Text>
            DeepMode is a Generative AI platform that helps creators make their AI clones. Creators can generate beautiful AI photos, whether realistic or non-realistic, with unlimited freedom. With DeepMode, creators can unleash their imagination and create unique photos with unlimited freedom.
          </Text>
        ),
      },
      {
        question: "How much does DeepMode cost?",
        answer: (
          <Text>
            DeepMode offers various top-up plans: Check{" "}
            <Link style={{ textDecoration: "underline" }} href="/payments">
              this page
            </Link>{" "}
            for more details.
          </Text>
        ),
      },
      {
        question: "Can I try DeepMode for free?",
        answer: (
          <Text>
            Yes, every new user automatically gets 20 credits. However, to unlock training an AI model you will need to top-up your balance.
          </Text>
        ),
      },
      {
        question: "Are my DeepMode creations private?",
        answer: (
          <Text>
            Yes, your creations on DeepMode are kept private and only you can view them. You also have the option to delete specific models or images you don&apos;t like.
          </Text>
        ),
      },
      {
        question: "Does DeepMode store my uploaded images?",
        answer: (
          <Text>
            No, your uploaded photos are deleted immediately from our servers after training. In addition, the Model itself is being deleted from our servers after 30 days. If you wish to extend the model storage, you can do so.
          </Text>
        ),
      },
      {
        question: "Can I use my creations anywhere?",
        answer: (
          <Text>
            Yes. Your generated images are yours to use on any other platform.
          </Text>
        ),
      },
      {
        question: "How is DeepMode different from Midjourney, Stable Diffusion, or Dall-E?",
        answer: (
          <Text>
            Unlike other AI platforms such as Midjourney, Stable Diffusion, or DALL-E, DeepMode stands out for its exclusive focus on the generation of AI clones. DeepMode is committed to providing a fully uncensored experience, allowing users unlimited creative freedom without any content restrictions.
          </Text>
        ),
      },
      {
        question: "Does DeepMode run on the Cloud?",
        answer: (
          <Text>
            Yes, DeepMode runs in the cloud. This means that you can use the platform and view your generations across various devices.
          </Text>
        ),
      },
      {
        question: "Does DeepMode offer an Affiliate program?",
        answer: (
          <Text>
            Absolutely! Become a partner and earn cash rewards every month. <a href="https://deepmode.firstpromoter.com/" style={{ textDecoration: "underline" }} target="_blank">Find out more about our Affiliate Program.</a>
          </Text>
        ),
      },
      {
        question: "Can I create videos with DeepMode?",
        answer: (
          <Text>
            {"Currently, video generation is not supported. However, we're working on this feature and plan to introduce it soon."}
          </Text>
        ),
      },
      {
        question: "Can I get a refund?",
        answer: (
          <Text>
            {"We offer refunds for purchases made within the last 7 days, provided no credits have been used. If you believe you're eligible for a refund, please "}
            <a style={{ textDecoration: "underline" }} href="/contact-us">
              contact us here.
            </a>
          </Text>
        ),
      },
      {
        question: "How do I get in touch with the DeepMode team?",
        answer: (
          <Text>
            We usually hang out on{" "}
            <a style={{ textDecoration: "underline" }} href="https://discord.gg/CMwtccKthy">
              Discord
            </a>
            . You can also reach out to us via{" "}
            <a style={{ textDecoration: "underline" }} href="mailto:info@deepmode.com">
              e-mail
            </a>
            .
          </Text>
        ),
      },
      {
        question: "Can I access DeepMode through an API?",
        answer: (
          <Text>
            {"Yes, you can access DeepMode through API and seamlessly integrate it into your application. "}
            <a style={{ textDecoration: "underline" }} href="/docs">
              Check our API integration documentation.
            </a>
          </Text>
        ),
      },
    ];

 
return (
  <>
  <Flex
      mt="20px"
      mb="20px"
      p={{ base: "20px", md: "100px" }} // Add more padding for desktop view
      direction="column"
  >
      <Text fontSize="40px" fontWeight="semibold">
          FAQs - Frequently Asked Questions
      </Text>
      <Accordion mt="10px" allowToggle>
          {faqData.map((item, index) => (
              <AccordionItem key={index}>
                  {({ isExpanded }) => (
                      <>
                          <AccordionButton>
                              <Box flex="1" textAlign="left">
                                  {item.question}
                              </Box>
                              {isExpanded ? <MinusIcon fontSize="12px" /> : <AddIcon fontSize="12px" />}
                          </AccordionButton>
                          <AccordionPanel pb={4}>{item.answer}</AccordionPanel>
                      </>
                  )}
              </AccordionItem>
          ))}
      </Accordion>
  </Flex>
</>
);
};

export default ModelPage;
